import { useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import { usePathname, useRouter } from 'expo-router';
import { trpc } from '../../lib/trpc';
import { trackOnboardingStepCompleted } from '../../lib/product-events';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import {
  OnboardingShell,
  Button,
  Card,
  LoadingSpinner,
  TransactionList,
  WebHead,
  CategoryRuleBuilder,
} from '../../components';

export default function Step3Transactions() {
  const router = useRouter();
  const pathname = usePathname();
  const isDesktop = useIsDesktop();
  const utils = trpc.useUtils();
  const advanceMutation = trpc.households.updateOnboardingStep.useMutation();
  const bulkMarkReviewedMutation = trpc.transactions.bulkMarkReviewed.useMutation();

  // Default to "needs review" so onboarding focuses the user on transactions
  // they actually need to verify. They can switch to "All" to scan everything.
  const [showAll, setShowAll] = useState(false);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);

  const txQ = trpc.transactions.list.useQuery({
    page: 1,
    pageSize: 50,
    ...(showAll ? {} : { needsReview: true as const }),
  });
  const transactions = txQ.data?.items ?? [];
  const totalShown = txQ.data?.total ?? 0;
  const reviewCountQ = trpc.transactions.reviewCount.useQuery();
  const remainingReviewCount = reviewCountQ.data?.count ?? (showAll ? undefined : totalShown);
  const visibleCount = transactions.length;
  const headerCountLabel =
    totalShown > visibleCount
      ? `showing ${visibleCount} of ${totalShown}`
      : `${totalShown}`;
  const headerLabel = showAll ? 'Recent transactions' : 'Needs review';
  const mobileHeaderLabel = showAll ? 'Recent' : 'Needs review';
  const detailLabel =
    totalShown > visibleCount
      ? `Showing the latest ${visibleCount} of ${totalShown}.`
      : `Review these ${visibleCount}.`;
  const continueLabel = remainingReviewCount === 0 ? 'Continue →' : 'Good enough for now →';
  const uncategorizedCount = transactions.filter((transaction) => !transaction.categoryName).length;
  const pendingCount = transactions.filter((transaction) => transaction.isPending).length;
  const handleContinue = async () => {
    await advanceMutation.mutateAsync({ step: 4 });
    trackOnboardingStepCompleted({
      route: pathname,
      stepNumber: 3,
      nextStepNumber: 4,
      completionKind: 'continue',
      metadata: {
        transactionCount: transactions.length,
        ...(remainingReviewCount !== undefined ? { remainingReviewCount } : {}),
        uncategorizedCount,
        pendingCount,
      },
    });
    router.replace('/(onboarding)/step-4-income' as any);
  };

  const refreshTransactions = async () => {
    await Promise.all([
      txQ.refetch(),
      reviewCountQ.refetch(),
    ]);
  };

  const handleMarkVisibleReviewed = async () => {
    const ids = transactions.map((transaction) => transaction.id);
    if (ids.length === 0) return;
    await bulkMarkReviewedMutation.mutateAsync({ ids });
    await Promise.all([
      utils.transactions.list.invalidate(),
      utils.transactions.reviewCount.invalidate(),
      refreshTransactions(),
    ]);
  };

  return (
    <>
    <OnboardingShell
      step={2}
      title="Verify Transaction Categories"
      subtitle="We've filtered to transactions that still need review. Tap any to set the right category — this directly improves your budget estimates in the next step."
      scrollable={false}
      footer={
        <Button
          testID="onboarding-transactions-continue-button"
          accessibilityLabel="Continue from transaction review"
          onPress={handleContinue}
          loading={advanceMutation.isPending}
        >
          {continueLabel}
        </Button>
      }
    >
      <WebHead><title>Step 3: Verify Transaction Categories – Per Diem</title></WebHead>
      {txQ.isLoading ? (
        <LoadingSpinner fullScreen={false} message="Loading transactions…" />
      ) : (
        <Card className="flex-1 overflow-hidden p-0">
          <View className="px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700 flex-row items-center justify-between gap-3">
            <View className="flex-1">
              <Text className="text-sm font-semibold text-gray-700 dark:text-gray-300" numberOfLines={1}>
                {isDesktop ? `${headerLabel} (${headerCountLabel})` : mobileHeaderLabel}
              </Text>
            </View>
            <Button
              testID="onboarding-transactions-create-rule-button"
              accessibilityLabel="Create a transaction categorization rule"
              variant="ghost"
              size="sm"
              onPress={() => setRuleModalOpen(true)}
            >
              Create Rule
            </Button>
            {/* Keep the toggle reachable even when the current view is empty —
                users with zero transactions needing review still need to be able
                to switch to "all" to verify nothing's wrong. */}
            <View className="flex-1 items-end">
              <Pressable
                testID="onboarding-transactions-show-all-toggle"
                accessibilityLabel={showAll ? 'Show transactions needing review' : 'Show all transactions'}
                onPress={() => setShowAll((v) => !v)}
                className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700"
              >
                <Text className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {showAll ? 'Needs review' : 'Show all'}
                </Text>
              </Pressable>
            </View>
          </View>
          {!showAll && transactions.length > 0 && (
            <View className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-row items-center justify-between gap-3">
              <Text className="text-xs text-gray-500 dark:text-gray-400 flex-1" numberOfLines={1}>
                {detailLabel}
              </Text>
              <Button
                testID="onboarding-transactions-mark-visible-reviewed-button"
                accessibilityLabel="Mark visible transactions reviewed"
                variant="ghost"
                size="sm"
                onPress={handleMarkVisibleReviewed}
                loading={bulkMarkReviewedMutation.isPending}
              >
                These {visibleCount} look right
              </Button>
            </View>
          )}
          {transactions.length === 0 ? (
            <View className="items-center py-8 px-4">
              <Text className="text-base font-semibold text-gray-600 dark:text-gray-400 text-center">
                {showAll ? 'No transactions yet' : 'Nothing left to review — nice work!'}
              </Text>
              {showAll && (
                <Text className="text-sm text-gray-400 mt-1 text-center">
                  Transactions will appear here once your accounts have synced.
                </Text>
              )}
            </View>
          ) : (
            <TransactionList
              transactions={transactions}
              onUpdated={refreshTransactions}
              emptyMessage="No transactions found."
            />
          )}
        </Card>
      )}
    </OnboardingShell>
    <CategoryRuleBuilder
      visible={ruleModalOpen}
      onClose={() => setRuleModalOpen(false)}
      onCreated={refreshTransactions}
    />
    </>
  );
}
